import { useMutation } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import { ordersCreateFulfillmentOrderConfirmationRequestMutation } from '@repo/sdk'

import { extractUserFacingMessage } from '../../providers/sdkErrorUtils'

import { useInvalidateFulfillmentOrders } from './useInvalidateFulfillmentOrders'

/**
 * Asks the buyer to confirm the purchase (#46 U2, #75). Delivery completion is no longer a dealer
 * action — the 06:00 batch marks parcels delivered — so this replaces the old "배송완료 처리".
 * The server decides eligibility (once per 24 hours, only while the order is on its way to
 * automatic confirmation) and answers 400 with a Korean message otherwise.
 */
export function useRequestConfirmation() {
  const invalidateFulfillmentOrders = useInvalidateFulfillmentOrders()
  const { message } = App.useApp()
  const { t } = useTranslation()

  const { mutate, isPending } = useMutation({
    ...ordersCreateFulfillmentOrderConfirmationRequestMutation(),
    onSuccess: async (_data, variables) => {
      message.success(t('common.modals.requestConfirmationSuccess'))
      await invalidateFulfillmentOrders(variables.path.fulfillmentOrderId)
    },
    onError: async (error, variables) => {
      message.error(extractUserFacingMessage(error) ?? t('common.modals.requestConfirmationFailed'))
      // `canRequestConfirmation` may have changed under us (24-hour limit, request resolved).
      await invalidateFulfillmentOrders(variables.path.fulfillmentOrderId)
    },
  })

  const requestConfirmation = (fulfillmentOrderId: string) => {
    mutate({ path: { fulfillmentOrderId } })
  }

  return { requestConfirmation, isRequesting: isPending }
}
